import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Checkbox from '@/design_system/Checkbox';
import Dialog from '@/design_system/Dialog';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { RadioBox, RadioBoxGroup } from '@/design_system/Radio/RadioBox/RadioBox';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import { JOB_REFUSED_REASONS, JobRefusedReason, useRefuseAnalysis } from '@/models/article';
import { useWorkshops } from '@/models/workshop';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { formatCurrency } from '@/utils/number';
import useViewPort from '@/utils/useViewport';

export const RefuseAnalysisDialog = ({
  isOpen,
  setIsOpen,
  onActionDone,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onActionDone: () => void;
}) => {
  const { t } = useLingui();
  const { iam } = useArticleContext();

  return (
    <Dialog
      title={
        !iam.hasUserScopeOnArticle('workshop')
          ? t({ id: 'refuse-analysis-modal.title.on-behalf', message: 'Workshop refusal' })
          : t({ id: 'refuse-analysis-modal.title', message: 'Refuse the job' })
      }
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <RefuseAnalysisForm
        onActionDone={() => {
          setIsOpen(false);
          onActionDone();
        }}
      />
    </Dialog>
  );
};

const RefuseAnalysisForm = ({ onActionDone }: { onActionDone: () => void }) => {
  const { isMobile } = useViewPort();
  const { i18n, t } = useLingui();
  const { article, iam } = useArticleContext();

  const { data: { workshops } = { workshops: [] } } = useWorkshops({
    ids: [article.workshopId!],
    articleId: article.id,
    limit: 1,
  });

  const cancellationFeeAgreementPrice = workshops?.[0]?.feeAgreementPrices?.find(
    (agreementPrice) => agreementPrice.feeType.slug === 'cancellation'
  );

  const cancellationFeeAmount = cancellationFeeAgreementPrice?.amount
    ? formatCurrency(cancellationFeeAgreementPrice.amount, cancellationFeeAgreementPrice.currency)
    : undefined;

  const { mutateAsync: refuseAnalysis, isPending } = useRefuseAnalysis({ articleId: article.id });

  const [showErrors, setShowErrors] = useState(false);

  const [reason, setReason] = useState<JobRefusedReason | undefined>();
  const [comment, setComment] = useState('');
  const [chargeCancellationFees, setChargeCancellationFees] = useState(false);

  const save = async () => {
    setShowErrors(true);

    if (!reason || !reasonData || (reasonData.requireComment && !comment)) {
      return;
    }

    await refuseAnalysis({
      reason,
      comment: comment || undefined,
      chargeCancellationFees,
    });

    onActionDone();
  };

  const reasonData = JOB_REFUSED_REASONS.find(({ id }) => id === reason);

  const workshopName = article.workshop?.name;
  const showCancellationFeeCheckbox = !!cancellationFeeAmount;

  return (
    <>
      <main>
        <Stack gap="1.5rem">
          <Stack gap="1rem">
            <Paragraph>
              {!iam.hasUserScopeOnArticle('workshop') ? (
                <Trans id="refuse-analysis-model.intro.on-behalf">
                  You're about to refuse the job on behalf of {workshopName}. Please specify the
                  reason for the refusal:
                </Trans>
              ) : (
                <Trans id="refuse-analysis-model.intro">
                  You're about to refuse the job. Please specify your refusal reason:
                </Trans>
              )}
            </Paragraph>

            <RadioBoxGroup
              name="refusal-reason"
              ariaLabel={t({
                id: 'refuse-analysis-model.reason.label',
                message: 'Refusal reason',
              })}
              isInvalid={showErrors && !reason}
              value={reason ?? 'none'}
              onChange={(value) => {
                setReason(value as JobRefusedReason);

                if (JOB_REFUSED_REASONS.find(({ id }) => id === value)?.disableCancellationFees) {
                  setChargeCancellationFees(false);
                }
              }}
              direction="column"
            >
              {JOB_REFUSED_REASONS.map(({ id, label, onBehalfLabel }) => (
                <RadioBox
                  key={id}
                  value={id}
                  ariaLabel={i18n._(!iam.hasUserScopeOnArticle('workshop') ? onBehalfLabel : label)}
                  title={i18n._(!iam.hasUserScopeOnArticle('workshop') ? onBehalfLabel : label)}
                />
              ))}
            </RadioBoxGroup>
          </Stack>

          <TextArea
            label={
              iam.hasUserScopeOnArticle('workshop') && (
                <>
                  <Trans id="refuse-analysis-model.comment.label">Comment</Trans>
                  {!reasonData?.requireComment && <OptionalLabel />}
                </>
              )
            }
            placeholder={
              !iam.hasUserScopeOnArticle('workshop')
                ? t({
                    id: 'refuse-analysis-modal.comment.placeholder.on-behalf',
                    message: 'Write the refusal reason...',
                  })
                : t({
                    id: 'refuse-analysis-modal.comment.placeholder',
                    message: 'Write a comment to explain your refusal...',
                  })
            }
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            error={
              showErrors && !!reasonData && reasonData.requireComment && !comment
                ? t({
                    id: 'refuse-analysis-modal.comment-error',
                    message: 'Please write a comment',
                  })
                : undefined
            }
            rows={3}
          />
        </Stack>
      </main>
      <footer>
        <Stack
          style={{ flex: 1 }}
          {...(isMobile
            ? {
                gap: '0.75rem',
                alignItems: 'stretch',
              }
            : {
                row: true,
                gap: '1rem',
                alignItems: 'center',
                justifyContent: showCancellationFeeCheckbox ? 'space-between' : 'flex-end',
              })}
        >
          {showCancellationFeeCheckbox && (
            <Checkbox
              isSelected={chargeCancellationFees}
              isDisabled={reasonData?.disableCancellationFees}
              onChange={setChargeCancellationFees}
            >
              {!iam.hasUserScopeOnArticle('workshop') ? (
                <Trans id="refuse-analysis-modal.charge-cancellation-fee.on-behalf">
                  Charge a {cancellationFeeAmount} cancellation fee on behalf of {workshopName} for
                  this item
                </Trans>
              ) : (
                <Trans id="refuse-analysis-modal.charge-cancellation-fee">
                  Charge a {cancellationFeeAmount} cancellation fee for this item
                </Trans>
              )}
            </Checkbox>
          )}
          <Button variant="danger" size="medium" onPress={save} isLoading={isPending}>
            <Trans id="refuse-analysis-modal.refuse">Refuse the job</Trans>
          </Button>
        </Stack>
      </footer>
    </>
  );
};
